import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { BasketItem } from '@portal/types/nexudus';
import { allResourcesQueryConfig } from './useAllResourcesQuery';

export const useBookingCancellation = (
  data: { items: BasketItem[] },
  accessToken: string,
  host: string,
) => {
  const query = useQuery({
    ...allResourcesQueryConfig(accessToken, host),
    select: (resources) => {
      const bookingItems = data.items.filter((item) => item.type === 'booking');

      // Determine if any of the bookings are past the cancellation deadline
      const hasUncancellableBooking = bookingItems.some((item) => {
        const resource = resources.find((r) => r.Id === item.data?.ResourceId);
        const policyMinutes = resource?.LateCancellationLimit ?? 0;

        // new Date can handle both ISO and RFC2822 formats, just in case Nexudus changes the format in the future
        const nativeTime = new Date(item.data?.FromTime);
        const startTime = DateTime.fromRFC2822(nativeTime.toUTCString());

        // If the date is invalid, log a warning and consider it as past the cancellation deadline to be safe
        if (!startTime.isValid) {
          console.warn(
            `Invalid date format for booking item with ResourceId ${item.data?.ResourceId}: ${item.data?.FromTime}`,
          );
          return true;
        }

        const deadline = startTime.minus({ minutes: policyMinutes });

        return DateTime.now() > deadline;
      });

      // Find the longest cancellation policy among the resources in the basket
      const longestPolicy = Math.max(
        0,
        ...resources
          .filter((r) =>
            bookingItems.some((item) => item.data.ResourceId === r.Id),
          )
          .map((r) => r.LateCancellationLimit ?? 0),
      );

      return {
        longestPolicy,
        canCancelBooking: !hasUncancellableBooking,
      };
    },
  });

  return {
    cancellationPolicyMinutes: query.data?.longestPolicy ?? 0,
    canCancelBooking: query.data?.canCancelBooking ?? true,
    isSuccess: query.isSuccess,
  };
};
